import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  WhatsApp as WhatsAppIcon,
  AutoAwesome,
} from '@mui/icons-material';
import { apiClient } from '../../services/api';

interface MessageTemplate {
  id: string;
  name: string;
  category?: string | null;
  body: string;
  isActive: boolean;
}

const blank: MessageTemplate = {
  id: '',
  name: '',
  category: '',
  body: '',
  isActive: true,
};

const MessageTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<MessageTemplate>(blank);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{ data?: { templates?: MessageTemplate[] } }>(
        '/campaigns/templates'
      );
      setTemplates(res?.data?.templates ?? []);
    } catch (e: unknown) {
      setSnack(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openDialog = (t?: MessageTemplate) => {
    setDraft(t ? { ...t } : { ...blank });
    setDialogOpen(true);
  };

  const saveDialog = async () => {
    if (!draft.name.trim() || !draft.body.trim()) return;
    setSaving(true);
    try {
      if (draft.id) {
        const res = await apiClient.patch<{ data?: { template?: MessageTemplate } }>(
          `/campaigns/templates/${draft.id}`,
          {
            name: draft.name,
            category: draft.category,
            body: draft.body,
            isActive: draft.isActive,
          }
        );
        const updated = res?.data?.template;
        if (updated) {
          setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
        }
      } else {
        const res = await apiClient.post<{ data?: { template?: MessageTemplate } }>(
          '/campaigns/templates',
          {
            name: draft.name,
            category: draft.category,
            body: draft.body,
            isActive: draft.isActive,
          }
        );
        const created = res?.data?.template;
        if (created) setTemplates((prev) => [...prev, created]);
      }
      setDialogOpen(false);
      setSnack('Saved');
    } catch (e: unknown) {
      setSnack(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const snapshot = templates;
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    try {
      await apiClient.delete(`/campaigns/templates/${id}`);
      setSnack('Deleted');
    } catch (e: unknown) {
      setTemplates(snapshot);
      setSnack(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await apiClient.post<{ data?: { count?: number } }>(
        '/campaigns/templates/seed-defaults'
      );
      const count = res?.data?.count ?? 0;
      if (count === 0) {
        setSnack('You already have templates — defaults skipped');
      } else {
        setSnack(`Added ${count} default templates`);
        void load();
      }
    } catch (e: unknown) {
      setSnack(e instanceof Error ? e.message : 'Seeding failed');
    }
  };

  const handleCopy = async (body: string) => {
    try {
      await window.navigator.clipboard.writeText(body);
      setSnack('Copied to clipboard');
    } catch {
      setSnack('Copy failed');
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#5C2A30' }}>
            Quick-reply templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Copy any message and paste into your WhatsApp Business App Quick Replies.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          {templates.length === 0 && !loading && (
            <Button
              startIcon={<AutoAwesome />}
              variant="outlined"
              onClick={() => void handleSeedDefaults()}
              sx={{
                borderColor: '#E8B4B8',
                color: '#5C2A30',
                textTransform: 'none',
                '&:hover': { borderColor: '#D69BA0', bgcolor: 'rgba(232,180,184,0.08)' },
              }}
            >
              Add starter pack
            </Button>
          )}
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => openDialog()}
            sx={{
              bgcolor: '#E8B4B8',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#D69BA0' },
            }}
          >
            New template
          </Button>
        </Stack>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#E8B4B8' }} />
        </Box>
      )}

      {!loading && templates.length === 0 && (
        <Box
          sx={{
            border: '1px dashed rgba(0,0,0,0.12)',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            No templates yet
          </Typography>
          <Typography variant="body2">
            Add the starter pack to get appointment, birthday, and follow-up templates.
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {templates.map((t) => (
          <Grid item xs={12} md={6} key={t.id}>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#5C2A30' }}>
                      {t.name}
                    </Typography>
                    {t.category && (
                      <Chip
                        label={t.category}
                        size="small"
                        sx={{ mt: 0.5, bgcolor: 'rgba(232,180,184,0.18)', color: '#5C2A30' }}
                      />
                    )}
                  </Box>
                  <Tooltip title="Edit" arrow>
                    <IconButton size="small" onClick={() => openDialog(t)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton size="small" color="error" onClick={() => void handleDelete(t.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    bgcolor: '#FAFAFA',
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.04)',
                  }}
                >
                  {t.body}
                </Typography>
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  startIcon={<ContentCopy />}
                  variant="contained"
                  onClick={() => void handleCopy(t.body)}
                  sx={{
                    bgcolor: '#25D366',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#1FB957' },
                  }}
                >
                  Copy message
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{draft.id ? 'Edit template' : 'New template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              label="Template name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Appointment confirmed"
            />
            <TextField
              label="Category (optional)"
              value={draft.category ?? ''}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              placeholder="Appointment, Birthday, Follow-up…"
            />
            <TextField
              label="Message body"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              multiline
              minRows={4}
              helperText='Tip: use placeholders like {{name}} or {{date}} — the owner replaces them when sending.'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveDialog()}
            disabled={saving || !draft.name.trim() || !draft.body.trim()}
            sx={{ bgcolor: '#E8B4B8', '&:hover': { bgcolor: '#D69BA0' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default MessageTemplatesTab;
